/**
 * Send an Order and wait PO (BLU-1506): building -> sent. This version sends
 * nothing itself, so the dialog is an instruction, "Order {po} on
 * {supplier}'s website": the buyer checks each blank on the supplier's
 * website, turns on "Out at {supplier}" for any the supplier does not have
 * (those orders keep waiting and the blank moves to the next purchase order),
 * buys the rest there, then presses Mark as sent.
 *
 * Blanks that were out last time start turned on (the mock-up's sticky "out"),
 * so a blank that is still out is not sent by accident; the buyer turns it off
 * once the supplier has it again. A footer line says what Send will do, live.
 *
 * Opened from the To buy card and from the PO page's action bar, never from
 * inside another dialog (DESIGN.md section 10). The body mounts on open, so
 * the toggles start fresh every time.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Send } from 'lucide-react';
import type { OrderAndWaitPo } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { cn } from '@/lib/utils';
import { useSendOrderAndWait } from '../hooks/useOrderAndWait';
import { blankLines, initialOutSkuIds, previewSend } from '../lib/order-and-wait';
import { jitRowName } from '../lib/jit-lines';

interface SendOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The building PO; null keeps the dialog closed. */
  po: OrderAndWaitPo | null;
}

export function SendOrderDialog({ open, onOpenChange, po }: SendOrderDialogProps) {
  const { send, pendingPoId } = useSendOrderAndWait();
  const pending = po !== null && pendingPoId === po.poId;
  return (
    <Dialog open={open && po !== null} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        {po && (
          <SendOrderBody
            key={po.poId}
            po={po}
            pending={pending}
            onCancel={() => onOpenChange(false)}
            onSend={async (outSkuIds) => {
              const done = await send(
                { poId: po.poId, poNumber: po.poNumber, supplierName: po.supplierName },
                outSkuIds,
              );
              if (done) onOpenChange(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendOrderBody({
  po,
  pending,
  onCancel,
  onSend,
}: {
  po: OrderAndWaitPo;
  pending: boolean;
  onCancel: () => void;
  onSend: (outSkuIds: string[]) => Promise<void>;
}) {
  const { t } = useTranslation('orders');
  const supplier = po.supplierName?.trim() || t('oaw.yourSupplier');
  const lines = useMemo(() => blankLines(po.lines), [po.lines]);
  const [out, setOut] = useState<ReadonlySet<string>>(() => new Set(initialOutSkuIds(lines)));
  const preview = previewSend(lines, out);
  const nothingToSend = preview.sendUnits === 0;
  const previewId = `oaw-send-preview-${po.poId}`;

  const toggle = (skuId: string, isOut: boolean) =>
    setOut((current) => {
      const next = new Set(current);
      if (isOut) next.add(skuId);
      else next.delete(skuId);
      return next;
    });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('oaw.send.title', { po: po.poNumber, supplier })}</DialogTitle>
        <DialogDescription>{t('oaw.send.body', { supplier })}</DialogDescription>
      </DialogHeader>

      {lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('oaw.card.empty')}</p>
      ) : (
        <ul className="divide-y rounded-lg border" aria-label={t('oaw.send.linesLabel')}>
          {lines.map((line) => {
            const name = jitRowName(line);
            const isOut = out.has(line.skuId);
            const switchId = `oaw-out-${po.poId}-${line.skuId}`;
            return (
              <li key={line.skuId} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5">
                <div className={cn('min-w-0 flex-1', isOut && 'text-muted-foreground')}>
                  <p className="break-words text-sm font-medium">{name}</p>
                  <p className="flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                    {line.sku && <span className="min-w-0 break-all font-mono">{line.sku}</span>}
                    {line.outUnits > 0 && <span>{t('oaw.send.outLastTime')}</span>}
                  </p>
                </div>
                <span className="w-12 shrink-0 text-right text-sm tabular-nums">
                  <span className="sr-only">{t('oaw.send.qtyAria', { count: line.qty })}</span>
                  <span aria-hidden="true">{line.qty}</span>
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    id={switchId}
                    checked={isOut}
                    onCheckedChange={(checked) => toggle(line.skuId, checked)}
                    disabled={pending}
                    aria-label={t('oaw.send.outAria', { supplier, name })}
                  />
                  <label htmlFor={switchId} className="cursor-pointer text-xs text-muted-foreground" aria-hidden="true">
                    {t('oaw.status.out', { supplier })}
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DialogFooter className="sm:items-center">
        <div id={previewId} role="status" className="text-sm text-muted-foreground sm:mr-auto">
          {nothingToSend ? (
            <NoteLine tone="warning">{t('oaw.send.nothing')}</NoteLine>
          ) : (
            <>
              {t('oaw.send.previewSend', { count: preview.sendUnits })}
              {preview.outUnits > 0 && <> {t('oaw.send.previewOut', { count: preview.outUnits })}</>}
            </>
          )}
        </div>
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          {t('oaw.dialog.back')}
        </Button>
        <Button
          type="button"
          onClick={() => void onSend([...out])}
          disabled={pending || nothingToSend}
          aria-busy={pending || undefined}
          aria-describedby={previewId}
          className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          ) : (
            <Send className="h-4 w-4" aria-hidden="true" />
          )}
          {t('oaw.send.confirm')}
        </Button>
      </DialogFooter>
    </>
  );
}
